import { useRef, useState } from "react";

/**
 * Hover-to-open for a Radix menu (Dropdown/Popover). Returns controlled `open`
 * state + pointer handlers to spread on the trigger, and `contentProps` for the
 * (portaled) content — the close delay bridges the gap between them. Mouse-only:
 * touch still falls through to Radix's click behavior.
 *
 * `contentProps` also suppresses Radix's open-autofocus when the menu was opened
 * by hover: focusing the first item scrolls it into view, which yanks the page
 * (the sticky navbar sits above the scroll position). Keyboard opens keep focus.
 *
 * ponytail: 120ms close delay is the bridge tuning knob; raise if the pointer
 * gap between trigger and content feels twitchy.
 */
export function useHoverMenu(delay = 120) {
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const viaHover = useRef(false);

  const enter = (e: React.PointerEvent) => {
    if (e.pointerType !== "mouse") return;
    clearTimeout(timer.current);
    viaHover.current = true;
    setOpen(true);
  };
  const leave = (e: React.PointerEvent) => {
    if (e.pointerType !== "mouse") return;
    timer.current = setTimeout(() => setOpen(false), delay);
  };

  const hoverProps = { onPointerEnter: enter, onPointerLeave: leave };

  return {
    open,
    // Radix drives this on click/keyboard/escape. Only an OPEN it drives is a
    // non-hover open; a close keeps the flag so the close-autofocus stays
    // suppressed for a menu the mouse opened (e.g. clicking an item).
    setOpen: (v: boolean) => {
      if (v) viaHover.current = false;
      setOpen(v);
    },
    hoverProps,
    contentProps: {
      ...hoverProps,
      onOpenAutoFocus: (e: Event) => {
        if (viaHover.current) e.preventDefault();
      },
      // Radix refocuses the trigger on close with a bare focus() (no
      // preventScroll). The trigger sits in a sticky navbar, so the browser
      // scrolls to its STATIC layout position — the page yanks to the top.
      // Mouse users never needed that focus restore; keyboard users keep it.
      onCloseAutoFocus: (e: Event) => {
        if (viaHover.current) e.preventDefault();
      },
    },
  };
}
